import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "../styles/DefaultLayout.scss";
import { createContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import SEO from "components/SEO";

import NavigationBar from "../components/Home/NavigationBar";
import CloudNavigation from "../components/CloudNavigation/CloudNavigation";
import SidebarFinished from "../components/CloudNavigation/CloudNavigationFinished";

import CookieBanner from "../components/Cookie/CookieBanner";
import CookieSettings from "../components/Cookie/CookieSettings";
import PageHeader from "../components/ui/PageHeader";
import Footer from "../components/Footer/Footer";
import CTASection from "../components/CTA/CTASection";

import FloatingCTA from "../components/CTA/FloatingCTA";
import ContactModal from "../components/CTA/ContactModal";
import ConsultationModal from "../components/CTA/ConsultationModal";
import { loadGoogleAds } from "../lib/consentScripts";

import { useActivityTracker } from "../hooks/useActivityTracker";
export const OutletContext = createContext<{
  setContactOpen: (isOpen: boolean) => void;
  setConsultOpen: (isOpen: boolean) => void;
}>(null!);

const DefaultLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const ctaRef = useRef<HTMLDivElement>(null);

  /* ------------------ routing ------------------ */
  const routeMap: Record<string, string> = {
    "c1-svg": "/bpo",
    "c2-svg": "/address-finder",
    "c3-svg": "/web-development",
  };

  const CLOUD_ROUTES = [
    "/bpo",
    "/address-finder",
    "/web-development",
    ];

    const isCloudRoute = CLOUD_ROUTES.some(
    (path) =>
        location.pathname === path ||
        location.pathname.startsWith(`${path}/`)
    );


  const [isFirstInitial, setIsFirstInitial] = useState(true);

  const HEADER_ENTER_DELAY = 0.65;
  const OUTLET_ENTER_DELAY = isFirstInitial
    ? HEADER_ENTER_DELAY + 0.1
    : HEADER_ENTER_DELAY + 0.7;

  /* ------------------ cookies ------------------ */
 const [cookieConsent, setCookieConsent] = useState<{
  analytics: boolean;
} | null>(null);

  const [hasDecidedCookies, setHasDecidedCookies] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCookieDelayed, setShowCookieDelayed] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem("cookie-consent");
    if (stored) {
      setCookieConsent(JSON.parse(stored));
      setHasDecidedCookies(true);
    }
  }, []);

 const saveConsent = (consent: { analytics: boolean }) => {
    setCookieConsent(consent);
    setHasDecidedCookies(true);
    sessionStorage.setItem("cookie-consent", JSON.stringify(consent));
  };

  useEffect(() => {
  if (cookieConsent?.analytics) {
    loadGoogleAds();
  }
}, [cookieConsent]);
  /* ------------------ skip / intro ------------------ */
  const [hasSkipped, setHasSkipped] = useState(false);
  const [isBlocked, setIsBlocked] = useState(true);

  useEffect(() => {
    if (hasSkipped) {
      setIsBlocked(false);
      return;
    }

    const timer = setTimeout(() => setIsBlocked(false), 14000);
    return () => clearTimeout(timer);
  }, [hasSkipped]);

  /* ------------------ header logic ------------------ */
  const HEADER_ROUTE_PATTERNS = [
    /^\/$/,
    /^\/bpo$/,
    /^\/address-finder$/,
    /^\/web-development(\/.*)?$/,
    /^\/about-us$/,
    /^\/blogs(\/.*)?$/,
    /^\/success-stories(\/.*)?$/,
    /^\/privacy-policy$/,
    /^\/terms-and-conditions$/,
  ];

  const HEADER_TITLES: { pattern: RegExp; title: string }[] = [
    { pattern: /^\/$/, title: "What we do" },
    { pattern: /^\/bpo$/, title: "BPO Services" },
    { pattern: /^\/address-finder$/, title: "Address Finder" },
    { pattern: /^\/web-development(\/.*)?$/, title: "Web Development" },
    { pattern: /^\/about-us$/, title: "About Us" },
    { pattern: /^\/blogs(\/.*)?$/, title: "Blogs" },
    { pattern: /^\/success-stories(\/.*)?$/, title: "Success Stories" },
    { pattern: /^\/privacy-policy$/, title: "Privacy Policy" },
    { pattern: /^\/terms-and-conditions$/, title: "Terms and Conditions" },
  ];

  const getHeaderTitle = (pathname: string) =>
    HEADER_TITLES.find(({ pattern }) => pattern.test(pathname))?.title ?? null;

  const showHeader = HEADER_ROUTE_PATTERNS.some((p) =>
    p.test(location.pathname)
  );
  const headerTitle = getHeaderTitle(location.pathname);

  const isNotService = [
    "/",
    "/about-us",
    "/blogs",
    "/success-stories",
    "/privacy-policy",
    "/terms-and-conditions",
  ].some(
    (path) =>
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`)
  );
const SEO_META: { pattern: RegExp; title: string; description: string }[] = [
  {
    pattern: /^\/$/,
    title: "Web & Data Solutions | Consumer Cloud Services Ltd",
    description: "Consumer Cloud Services provide email and consumer data management solutions across all channels for your data-driven marketing needs.",
  },
  {
    pattern: /^\/bpo$/,
    title: "BPO – Outsourcing Solutions | Consumer Cloud Services",
    description: "We provide contracted and project-based services along with specialist personnel all focused on data for marketing purposes.",
  },
  {
    pattern: /^\/address-finder$/,
    title: "Cost-Saving Address Lookups | Consumer Cloud Services",
    description: "We provide contracted and project-based services along with specialist personnel all focused on data for marketing purposes.",
  },
  {
    pattern: /^\/web-development(\/.*)?$/,
    title: "Web Development - Scalable, Secure Solutions | Consumer Cloud Services",
    description: "We are highly experienced with consumer data and web with our web development team that includes C++, Java, .Net and PHP expertise.",
  },
  {
    pattern: /^\/about-us$/,
    title: "About Us - Scalable Data Solutions | Consumer Cloud Services",
    description: "Learn about Consumer Cloud Services Ltd, delivering powerful data solutions for businesses worldwide. Trusted for over two decades.",
  },
  {
    pattern: /^\/blogs(\/.*)?$/,
    title: "Latest Industry Insights | Consumer Cloud Services Ltd",
    description: "Stay updated with the latest blogs from Consumer Cloud Services Ltd featuring insights, and trends in cloud technology, digital solutions, and IT services",
  },
  {
    pattern: /^\/success-stories(\/.*)?$/,
    title: "Customer Success Stories | Consumer Cloud Services Ltd",
    description: "Explore our customer success stories to see how Consumer Cloud Services  helps businesses grow with reliable cloud solutions, technology, and expert support.",
  },
  {
    pattern: /^\/terms-and-conditions$/,
    title: "Terms and Conditions | Consumer Cloud Services Ltd",
    description: "We provide contracted and project-based services along with specialist personnel all focused on data for marketing purposes.",
  },
  {
    pattern: /^\/privacy-policy$/,
    title: "Privacy Policy | Consumer Cloud Services Ltd",
    description: "Read our Privacy Policy to learn how we collect, use, protect, and manage your personal information when you use our website and services.",
  },
];

  /* ------------------ CTA modals ------------------ */
  const [contactOpen, setContactOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);

  /* ------------------ intro delay ------------------ */
  const [showHeaderDelayed, setShowHeaderDelayed] = useState(false);
  const [showContentDelayed, setShowContentDelayed] = useState(false);
  const [showCTADelayed, setShowCTADelayed] = useState(false);

  useEffect(() => {
    if (hasSkipped) {
      setShowHeaderDelayed(true);
      setShowContentDelayed(true);
      setShowCTADelayed(true);
      setShowCookieDelayed(true);
      return;
    }

    const h = setTimeout(() => setShowHeaderDelayed(true), 11000);
    const c = setTimeout(() => setShowContentDelayed(true), 12000);
    const f = setTimeout(() => setShowCTADelayed(true), 13000);
    const ck = setTimeout(() => setShowCookieDelayed(true), 15000);

    return () => {
      clearTimeout(h);
      clearTimeout(c);
      clearTimeout(f);
      clearTimeout(ck);
    };
  }, [hasSkipped]);

  /* ------------------ per-route sequencing ------------------ */
  const [headerReady, setHeaderReady] = useState(false);
const { trackActivity } = useActivityTracker();
  useEffect(() => {
    setHeaderReady(false);
    const t = setTimeout(() => setHeaderReady(true), hasSkipped ? 120 : 400);
    return () => clearTimeout(t);
  }, [location.pathname, hasSkipped]);

  /* ------------------ navigation ------------------ */
  const handleCircleSelect = (id: string) => {
    const route = routeMap[id];
    if (!route) return;
    setTimeout(() => navigate(route), hasSkipped ? 0 : 600);
  };
 const currentSEO = SEO_META.find(({ pattern }) =>
    pattern.test(location.pathname)
  );
  return (
    <OutletContext.Provider value={{ setConsultOpen, setContactOpen }}>
      <div className="min-h-screen max-w-[1440px] mx-auto flex flex-col relative">
        {/* SKIP */}
         {currentSEO && (
          <SEO
            title={currentSEO.title}
            description={currentSEO.description}
            canonical={location.pathname}
          />
        )}
        {isBlocked && !hasSkipped && (
          <button
            onClick={() => {
              setHasSkipped(true);
              setIsBlocked(false);
            }}
            className="fixed bottom-6 right-6 z-[10001] px-5 py-2.5 text-sm font-semibold bg-black text-white rounded-full shadow-lg"
          >
            Skip
          </button>
        )}

        {isBlocked && <div className="fixed inset-0 z-[9999]" />}

        {!hasSkipped && (
          <CloudNavigation onCircleSelect={handleCircleSelect} showLabels={isCloudRoute} />
        )}
        {hasSkipped && (
          <SidebarFinished onCircleSelect={handleCircleSelect}  />
        )}

        <NavigationBar hasSkipped={hasSkipped}  />

        <main className="flex flex-col flex-1">
          <AnimatePresence mode="wait" initial={false}>
            {showHeaderDelayed && (
              <motion.div key={location.pathname}>
                {showHeader && headerTitle && (
                  <PageHeader
                    title={headerTitle}
                    isNotService={isNotService}
                  />
                )}

                {showContentDelayed && headerReady && (
                  <motion.div
                    initial={{ opacity: 0, y: -24 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: { delay: OUTLET_ENTER_DELAY },
                    }}
                    onAnimationComplete={() => {
                      if (isFirstInitial) setIsFirstInitial(false);
                    }}
                    exit={{
                      opacity: 0,
                      y: -24,
                      transition: { duration: 0.25, ease: "easeInOut" },
                    }}
                  >
                    <Outlet />
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        {/* CTA + FOOTER */}
        {showCTADelayed && (
          <>
            <FloatingCTA
                onContactClick={() => {
                    setContactOpen(true);
                    trackActivity(
                    "CTA_Click",
                    "Contact Modal Opened",
                    "User clicked floating contact CTA"
                    );
                }}
                onConsultClick={() => {
                    setConsultOpen(true);
                    trackActivity(
                    "CTA_Click",
                    "Consultation Modal Opened",
                    "User clicked floating consultation CTA"
                    );
                }}
                targetRef={ctaRef}
                />
            <div ref={ctaRef}>
              <CTASection />
            </div>

            <Footer />
          </>
        )}

        {/* COOKIE BANNER */}
        {!hasDecidedCookies && !showSettings && (
          <CookieBanner
           onAcceptAll={() =>
            saveConsent({ analytics: true })
            }
            onOpenSettings={() => setShowSettings(true)}
          />
        )}

        <CookieSettings
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          onSave={saveConsent}
        />

        <ContactModal
          isOpen={contactOpen}
          onClose={() => setContactOpen(false)}
        />
        <ConsultationModal
          isOpen={consultOpen}
          onClose={() => setConsultOpen(false)}
        />
      </div>
    </OutletContext.Provider>
  );
};

export default DefaultLayout;
